<template>
  <component
    :is="inNav ? 'li' : 'div'"
    :class="computedDropdownClasses"
    v-c-clickaway="visible ? hide : null"
    @click="checkClick($event)"
  >
    <slot name="toggler">
      <component
        :is="togglerTag"
        v-on="{ click: splittedToggler ? splitButtonClick : toggle }"
        :class="computedTogglerClasses"
        v-bind="splittedToggler ? {} : togglerAttrs"
      >
        <slot name="toggler-content">{{togglerText}}</slot>
      </component>
      <button
        v-if="splittedToggler"
        @click="toggle"
        class="dropdown-toggle dropdown-toggle-split"
        :class="computedTogglerClasses"
        v-bind="togglerAttrs"
      />
    </slot>
    <div ref="menu" :class="computedMenuClasses">
      <slot></slot>
    </div>
  </component>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { CLink } from './link'
import { CClickaway } from './directives'

export default {
  name: 'CDropdown',
  directives: { CClickaway },
  components: { CLink },
  props: {
    togglerText: { type: String, default: 'Dropdown' },
    show: Boolean,
    disabled: Boolean,
    addMenuClasses: [String, Array, Object],
    addTogglerClasses: [String, Array, Object],
    inNav: Boolean,
    caret: { type: Boolean, default: true },
    color: String,
    size: String,
    split: Boolean,
    placement: { type: String, default: 'bottom-start' },
    offset: { type: Array, default: () => [0, 0] },
    flip: { type: Boolean, default: true },
    customPopperOptions: Object
  },
  emits: ['update:show', 'click'],
  // CDropdownItem closes its parent dropdown through this
  provide () {
    return { coreuiDropdown: { close: () => this.hide() } }
  },
  data () {
    return { visible: this.show }
  },
  watch: {
    show (val) {
      this.visible = val
    },
    visible: {
      immediate: true,
      handler (val) {
        val ? this.createPopper() : this.removePopper()
        this.$emit('update:show', val)
      }
    }
  },
  beforeUnmount () {
    this.removePopper()
  },
  methods: {
    checkClick (e) {
      if (this.$slots.toggler && this.$el.firstElementChild && this.$el.firstElementChild.contains(e.target)) {
        this.toggle(e)
      }
    },
    hide () {
      this.visible = false
    },
    splitButtonClick () {
      this.visible = false
      this.$emit('click')
    },
    toggle (e) {
      e.preventDefault()
      this.visible = !this.visible
    },
    removePopper () {
      if (this._popper) this._popper.destroy()
      this._popper = null
    },
    createPopper () {
      this.removePopper()
      if (this.disabled) {
        this.visible = false
        return
      }
      this.$nextTick(() => {
        if (!this.$el || !this.$el.children) return
        this._popper = createPopper(
          this.$el.children[this.$el.children.length - 2],
          this.$refs.menu,
          this.customPopperOptions || this.defaultPopperOptions
        )
      })
    }
  },
  computed: {
    defaultPopperOptions () {
      return {
        placement: this.placement,
        modifiers: [
          { name: 'offset', options: { offset: this.offset } },
          { name: 'flip', enabled: this.flip },
          { name: 'preventOverflow', options: { padding: 10 } }
        ]
      }
    },
    carretClass () {
      return this.placement.includes('top') ? 'dropup'
        : this.placement.includes('right') ? 'dropright'
          : this.placement.includes('left') ? 'dropleft' : 'dropdown'
    },
    computedDropdownClasses () {
      return [this.carretClass, { show: this.visible, 'nav-item': this.inNav, 'btn-group': this.splittedToggler }]
    },
    togglerTag () {
      return this.inNav ? 'CLink' : 'button'
    },
    splittedToggler () {
      return this.split && !this.inNav
    },
    computedTogglerClasses () {
      return [
        this.addTogglerClasses,
        this.inNav ? 'nav-link' : 'btn',
        {
          'dropdown-toggle': this.caret && !this.split,
          [`btn-${this.size}`]: this.size && !this.inNav,
          disabled: this.disabled,
          [`${this.inNav ? 'bg' : 'btn'}-${this.color}`]: this.color
        }
      ]
    },
    togglerAttrs () {
      return {
        type: this.inNav ? null : 'button',
        'aria-expanded': this.visible ? 'true' : 'false',
        'aria-haspopup': 'true'
      }
    },
    computedMenuClasses () {
      return [this.addMenuClasses, 'dropdown-menu', { show: this.visible }]
    }
  }
}
</script>
